import React, { useState } from "react";
import Section from "./layouts/Section";
import { submitLead } from "../utils/leadSubmit";
import { track, EVENTS, trackPhoneCall } from "../utils/analytics";

/**
 * Device Health Check landing page (Growth Platform — Slice S3).
 *
 * A free diagnostic lead magnet (spec §21): promotes a multi-point inspection
 * and captures a lead via the shared EmailJS pipeline. Frontend-only — the
 * optional "inspection record" architecture is deferred to the MERN phase.
 */

const NAVY = "#18305d";
const ACCENT = "#ffaa19";

const CHECKS = [
  { icon: "fas fa-battery-three-quarters", label: "Battery Health" },
  { icon: "fas fa-hdd", label: "Storage & Memory" },
  { icon: "fas fa-plug", label: "Charging Port" },
  { icon: "fas fa-volume-up", label: "Speakers" },
  { icon: "fas fa-microphone", label: "Microphone" },
  { icon: "fas fa-camera", label: "Cameras" },
  { icon: "fas fa-fingerprint", label: "Face ID / Biometrics" },
  { icon: "fas fa-charging-station", label: "Wireless Charging" },
  { icon: "fas fa-mobile-alt", label: "Physical Condition" },
];

function DeviceHealthCheck() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", device: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [done, setDone] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canSubmit = form.name.trim() && form.email.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    submitLead({
      name: form.name,
      email: form.email,
      phone: form.phone,
      device: form.device || "Not specified",
      subject: "Free Device Health Check Request",
      message: `New Device Health Check request.\n\nDevice: ${
        form.device || "Not specified"
      }`,
    })
      .then(() => {
        track(EVENTS.HEALTH_CHECK_LEAD, { source: "device_health_check" });
        setDone(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message:
            "Something went wrong. Please try again or call us at (215) 939-3933.",
        });
        setIsSubmitting(false);
        // eslint-disable-next-line no-console
        console.log("FAILED...", error?.text);
      });
  };

  return (
    <Section title={"Free Device Health Check"}>
      <section className="section page-content">
        <div className="container">
          <div className="row justify-content-center mb-40">
            <div className="col-lg-9 text-center">
              <h2 className="text-dark mb-15">
                Is your phone healthy? Find out — free.
              </h2>
              <p className="text-muted" style={{ fontSize: "17px" }}>
                Bring your device in for a complimentary multi-point inspection.
                Our technicians check everything that matters and give you an
                honest report — no pressure, no obligation.
              </p>
            </div>
          </div>

          <div className="row">
            {/* Checklist */}
            <div className="col-lg-7 mb-4">
              <div
                style={{
                  background: "#f6f8fc",
                  borderRadius: "16px",
                  padding: "30px",
                  height: "100%",
                }}
              >
                <h4 className="text-dark mb-25">What we check</h4>
                <div className="row">
                  {CHECKS.map((c) => (
                    <div className="col-sm-6 mb-20" key={c.label}>
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "10px",
                            background: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            flexShrink: 0,
                          }}
                        >
                          <i className={c.icon} style={{ color: ACCENT }} />
                        </span>
                        <span style={{ fontWeight: 600, color: NAVY }}>
                          {c.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Lead form */}
            <div className="col-lg-5">
              {!done ? (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    padding: "30px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                  }}
                >
                  <h4 className="text-dark mb-10">Book your free check</h4>
                  <p className="text-muted mb-25">
                    Tell us who you are and we'll confirm the best time.
                  </p>

                  {status.message && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "18px",
                        background: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                      }}
                    >
                      <i className="fas fa-exclamation-circle mr-2" />
                      {status.message}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="form-group mb-20">
                      <label className="mb-2 font-w-600">Name *</label>
                      <input
                        className="form-control py-3 px-3"
                        type="text"
                        required
                        placeholder="Your name"
                        value={form.name}
                        onChange={(e) => update({ name: e.target.value })}
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label className="mb-2 font-w-600">Email *</label>
                      <input
                        className="form-control py-3 px-3"
                        type="email"
                        required
                        placeholder="Your email"
                        value={form.email}
                        onChange={(e) => update({ email: e.target.value })}
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label className="mb-2 font-w-600">Phone (optional)</label>
                      <input
                        className="form-control py-3 px-3"
                        type="tel"
                        placeholder="(215) 555-1234"
                        value={form.phone}
                        onChange={(e) => update({ phone: e.target.value })}
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label className="mb-2 font-w-600">Device (optional)</label>
                      <input
                        className="form-control py-3 px-3"
                        type="text"
                        placeholder="e.g. iPhone 13"
                        value={form.device}
                        onChange={(e) => update({ device: e.target.value })}
                      />
                    </div>
                    <button
                      className="btn btn-primary w-100"
                      type="submit"
                      disabled={isSubmitting || !canSubmit}
                      style={{ padding: "13px" }}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-heartbeat mr-2" />
                          Book My Free Check
                        </>
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    padding: "36px 30px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "#d4edda",
                      color: "#155724",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 18px",
                      fontSize: "30px",
                    }}
                  >
                    <i className="fas fa-check" />
                  </div>
                  <h4 className="text-dark mb-10">Request received!</h4>
                  <p className="text-muted mb-20">
                    Thanks {form.name.split(" ")[0] || "there"}! We'll reach out
                    shortly to confirm your free device health check.
                  </p>
                  <a
                    href="tel:+12159393933"
                    className="btn btn-primary"
                    onClick={() => trackPhoneCall("health_check_success")}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Call (215) 939-3933
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Section>
  );
}

export default DeviceHealthCheck;
